<template>
  <div class="codeInputView">
    <h2>Enter your 4 digit code to get started</h2>
    <div class="inputWrapper">
      <div v-for="(input, index) in Array(4)" :key="index">
        <input
          v-model="code[index]"
          :ref="(el) => (inputRefs[index] = el as HTMLInputElement)"
          @focus="() => jumpToFirstEmptyField(index)"
          @paste="($event) => onPaste($event as ClipboardEvent, index)"
          @keydown="($event) => onInput($event as KeyboardEvent, index)"
          class="input"
          type="number"
          maxlength="1"
          min="0"
          max="9"
          :disabled="checkingCode"
        />
      </div>
    </div>

    <div v-if="checkingCode" class="primary code-check-notice">
      <font-awesome-icon :icon="faSpinner" :size="'2x'" :spin="true" class="fast-spin spinner" />
      Checking Code...
    </div>

    <DemoWarningComponent
      class="demo-message"
      v-if="isDemoSession"
      @exit-demo="$emit('re-enter-code')"
    />

    <ErrorModal
      :show="!!errorMsg?.message"
      :error-msg="errorMsg"
      :regenerateScreenerId="regenerateScreenerId"
      @regenerate="emit('regenerate-code')"
      @retry="reEnterCode"
      @close="reEnterCode"
    />
  </div>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { onMounted, reactive, ref, computed, watch, nextTick } from 'vue'
// Types
import type { Ref, ComputedRef, PropType } from 'vue'
import type { Code, ErrorMessage } from '@/customTypes'

// Stores
import { storeToRefs } from 'pinia'
import { useDevStore } from '@/stores/DevStore'

// Components
import DemoWarningComponent from '@/components/DemoWarningComponent.vue'
import ErrorModal from '@/components/ErrorModal.vue'

// Props
const props = defineProps({
  initialCode: { type: String, required: false },
  errorMsg: {
    type: Object as PropType<ErrorMessage>,
    default: () => ({ message: '', type: 'danger' })
  },
  checkingCode: { type: Boolean, default: false },
  regenerateScreenerId: { type: Boolean, default: false }
})

// Emits
const emit = defineEmits<{
  (e: 'code-updated'): void
  (e: 'code-entered', code: string): void
  (e: 'regenerate-code'): void
  (e: 're-enter-code'): void
}>()

// Refs
const inputRefs: Ref<HTMLInputElement[]> = ref([])
const code: Code = reactive({
  0: '',
  1: '',
  2: '',
  3: ''
})
const devStore = useDevStore()
const { isDemoSession } = storeToRefs(devStore)

const checkingCode = computed(() => props.checkingCode)

onMounted(() => {
  if (props.initialCode) {
    parseCode(props.initialCode, 0)
  }
  inputRefs.value?.[0]?.focus()
})

/**
 *  Handles the input event on the input fields
 * @param {KeyboardEvent} event
 * @param {number} index
 */
const onInput = (event: KeyboardEvent, index: number) => {
  if (event.metaKey && event.key === 'v') return
  event.preventDefault()

  if (event.key.match(/[0-9]/)) {
    code[index] = event.key
    goForward(index)
    codeUpdated()
  }

  if (event.key === 'Backspace') {
    code[index] = ''
    goBack(index)
    codeUpdated()
  }

  if (event.key === 'ArrowLeft') {
    goBack(index)
  }

  if (event.key === 'ArrowRight' || event.key === 'Tab') {
    goForward(index)
  }
}

/**
 * Focus on the next input field
 * @param {number} index
 */
const goForward = (index: number) => {
  if (index < Object.keys(code).length - 1) {
    inputRefs.value?.[index + 1]?.focus()
  }
}

/**
 * Focus on the previous input field
 * @param {number} index
 */
const goBack = (index: number) => {
  if (index > 0) {
    inputRefs.value?.[index - 1]?.focus()
  }
}

/**
 * Jumps to the first empty field if user clicks on a field with preceding empty fields.
 * @param {number} index - The index of the currently focused field.
 */
const jumpToFirstEmptyField = (index: number) => {
  // Find the first empty field from the beginning up to the selected index
  for (let i = 0; i < index; i++) {
    if (code[i] === '') {
      inputRefs.value?.[i]?.focus()
      return
    }
  }
}

/**
 * Emit the code-entered event if all input fields are filled
 */
const codeUpdated = () => {
  if (Object.values(code).every((value) => value !== '')) {
    emit('code-entered', Object.values(code).join(''))
    inputRefs.value?.forEach((input) => {
      input.blur()
    })
  } else {
    emit('code-updated')
  }
}

/**
 * Handle paste event on the first input field
 * @param {ClipboardEvent} event
 * @param {number} index
 */
const onPaste = (event: ClipboardEvent, index: number) => {
  event.preventDefault()
  const clipboardData = event.clipboardData
  const pastedText = clipboardData?.getData('text').replace(/\s+/g, '').trim()
  if (pastedText) {
    parseCode(pastedText, index)
  }
}

/**
 * Handle paste event on the first input field
 * @param {string} inputCode
 * @param {number} index
 */
const parseCode = (inputCode: string, index: number) => {
  const numericCode = parseInt(inputCode.trim())
  if (!isNaN(numericCode)) {
    const characters = inputCode.split('')
    if (characters.length === 4) {
      characters.forEach((char, i) => {
        code[index + i] = char
      })
    }
  }
  codeUpdated()
}

/**
 * Re-enter the code but refocus on the last input field when returning to retry an entry
 */
const reEnterCode = () => {
  if (inputRefs.value.length > 0) {
    focusCodeInputField(inputRefs.value?.[3])
  }
  emit('re-enter-code')
}

/**
 * Focus on the input field and set the cursor to the end of the input value
 * @param {HTMLInputElement} inputElement
 */
const focusCodeInputField = (inputElement: HTMLInputElement) => {
  if (inputElement) {
    inputElement.focus()
    const value = inputElement.value
    inputElement.value = ''
    inputElement.value = value
  }
}
</script>

<style lang="scss" scoped>
.codeInputView {
  position: absolute;
  top: 15%;
  display: flex;
  flex-direction: column;
  align-items: center;

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type='number'] {
    appearance: textfield;
    -moz-appearance: textfield; /* Firefox */
  }

  h2 {
    margin-bottom: 3rem;
  }

  .inputWrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;

    .input {
      width: 5rem;
      height: 8rem;
      text-align: center;
      font-size: 4rem;
      border-radius: 5px;
      border: 2px solid $secondary;
      padding: 1rem;

      &:focus {
        outline: none;
        border: 2px solid $primary;
      }
    }
  }

  .code-check-notice {
    font-size: 1.5rem;
    display: flex;
    flex-direction: column;
    height: calc(var(--full-vh) * 0.2);
    justify-content: space-evenly;
    margin-top: 3rem;
    color: $secondary;

    .spinner {
      color: $primary;
    }
  }

  .demo-message {
    margin-top: 2rem;
  }
}
</style>
